import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { I, T } from "services/lang/LanguageService";
import { DataTable } from "primereact/datatable";
import { easasConfirmDialog } from "components/fragments/easas-global-fragment/EasasGlobalConfirmDialog";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import {
  useCreateDatasetMapperData,
  useDeleteDatasetMapperData,
  useGetDatasetMappersData,
  useUpdateDatasetMapperData,
} from "api/api-hooks/dataset-mapper-hooks";
import { PermissionGuard } from "components/wrappers/PermissionGuard";
import MapperMappings from "./MapperMappings";
import CreateEditDatasetMapper, { MapperEditorMode } from "./CreateEditDatasetMapper";
import { DatasetStringValueMapperDTO } from "../../../charts/interfaces";

const ManageDatasetMappers = () => {
  const [datasetMappersDialogVisibility, setDatasetMappersDialogVisibility] =
    useState<boolean>(false);
  const [editorMode, setEditorMode] = useState<MapperEditorMode | null>();
  const [mapperMappingDialogVisibility, setMapperMappingDialogVisibility] =
    useState<boolean>(false);
  const [selectedMapper, setSelectedMapper] = useState<DatasetStringValueMapperDTO>();
  const [mappers, setMappers] = useState<DatasetStringValueMapperDTO[]>([]);

  const {
    data: currentMappers,
    isLoading,
    isError,
    refetch: refetchMappers,
  } = useGetDatasetMappersData();

  const createMutation = useCreateDatasetMapperData();
  const updateMutation = useUpdateDatasetMapperData();
  const deleteMutation = useDeleteDatasetMapperData();

  useEffect(() => {
    if (!isLoading && !isError) {
      setMappers(currentMappers?.length ? [...currentMappers] : []);
    }
  }, [currentMappers, isLoading, isError]);

  useEffect(() => {
    refetchMappers();
  }, [refetchMappers]);

  const handleDeleteDialogConfirm = async (mapper: DatasetStringValueMapperDTO) => {
    const newMappersArray = mappers.filter(m => m.id !== mapper.id);
    await setMappers(newMappersArray);
    deleteMutation.mutate(mapper);
  };

  const handlerowDeleteClick = (mapper: DatasetStringValueMapperDTO) => {
    setSelectedMapper(mapper);
    easasConfirmDialog({
      icon: "pi pi-exclamation-triangle",
      header: T("confirm-dialog.confirmation"),
      message: T("confirm-dialog.are-you-sure-you-want-to-delete-this-mappings-group"),
      confirmedCallback: () => handleDeleteDialogConfirm(mapper),
    });
  };

  const handleRowEditClick = (mapper: DatasetStringValueMapperDTO) => {
    setSelectedMapper(mapper);
    handleOpenEditDialog();
  };

  const handleEditDialogSave = async (updatedMapper: DatasetStringValueMapperDTO) => {
    if (updatedMapper) {
      for (let i = 0; i < mappers.length; i++) {
        if (mappers[i].id === updatedMapper.id) {
          mappers[i] = updatedMapper;
          updateMutation.mutate(updatedMapper);
          break;
        }
      }
    }
    handleCloseEditDialog();
  };

  const createDatasetMapper = async (newMapper: DatasetStringValueMapperDTO) => {
    if (newMapper) {
      await setMappers([...mappers, newMapper]);
      createMutation.mutate(newMapper);
    }
    handleCloseEditDialog();
  };

  const handleOpenCreateDialog = () => {
    setEditorMode(MapperEditorMode.CREATE);
    setDatasetMappersDialogVisibility(true);
  };

  const handleOpenEditDialog = () => {
    setEditorMode(MapperEditorMode.EDIT);
    setDatasetMappersDialogVisibility(true);
  }

  const handleCloseEditDialog = () => {
    setEditorMode(null);
    setDatasetMappersDialogVisibility(false);
  }

  const mappingsDisplayTemplate = (rowData: DatasetStringValueMapperDTO) => {
    const handleMappingClick = () => {
      setSelectedMapper(rowData);
      setMapperMappingDialogVisibility(true);
    };
    return (
      <div>
        <div>
          {Object.keys(rowData.mappings).length === 0 ? (
            <h3>{T("word.no-mappings")}</h3>
          ) : (
            <ul>
              {Object.entries(rowData.mappings)
                .slice(0, 3)
                .map(([key, value]) => (
                  <li key={key}>
                    {key}: {typeof value === "string" ? I(value) : value}
                  </li>
                ))}
            </ul>
          )}
        </div>
        {Object.keys(rowData.mappings).length > 3 && (
          <Button
            type="button"
            label={T("action.see-all-mappings")}
            className="p-button-text p-0"
            onClick={handleMappingClick}
          />
        )}
      </div>
    );
  };

  const mapperEditButtonTemplate = (mapperData: DatasetStringValueMapperDTO) => {
    return (
      <>
        {mapperData.global && (
          <PermissionGuard permissions="ADMIN">
            <Button
              type="button"
              icon="pi pi-pencil"
              tooltip={T("action.edit-mappings")}
              className="p-button-rounded p-button-text p-ml-2"
              onClick={() => handleRowEditClick(mapperData)}
            />
          </PermissionGuard>
        )}
        {!mapperData.global && (
          <Button
            type="button"
            icon="pi pi-pencil"
            tooltip={T("action.edit-mappings")}
            className="p-button-rounded p-button-text p-ml-2"
            onClick={() => handleRowEditClick(mapperData)}
          />
        )}
      </>
    );
  };

  const mapperRemoveButtonTemplate = (mapperData: DatasetStringValueMapperDTO) => {
    return (
      <>
        {mapperData.global && (
          <PermissionGuard permissions="ADMIN">
            <Button
              type="button"
              icon="pi pi-trash"
              tooltip={T("action.remove-mapping-group")}
              className="const-del-btn  p-button-rounded p-button-text p-button-danger"
              onClick={() => handlerowDeleteClick(mapperData)}
            />
          </PermissionGuard>
        )}
        {!mapperData.global && (
          <Button
            type="button"
            icon="pi pi-trash"
            tooltip={T("action.remove-mapping-group")}
            className="const-del-btn  p-button-rounded p-button-text p-button-danger"
            onClick={() => handlerowDeleteClick(mapperData)}
          />
        )}
      </>
    );
  };

  return (
    <div>
      {isLoading && <div>{T("word.loading")}</div>}
      {isError && <div>{T("error.error-fetching-data")}</div>}
      {!isLoading && !isError && (
        <div>
          <DataTable
            value={mappers}
            editMode="row"
            dataKey="id"
            scrollHeight="300px"
            responsiveLayout="scroll"
            size="small"
          >
            <Column field="title" header={T("word.title")} />
            <Column
              header={T("word.global")}
              body={rowData => (rowData.global ? "Global" : "Not Global")}
            />
            <Column
              header={T("word.mappings")}
              body={(rowData: DatasetStringValueMapperDTO) => mappingsDisplayTemplate(rowData)}
            />
            <Column
              body={mapperData => mapperEditButtonTemplate(mapperData)}
              style={{ width: "3%" }}
            />
            <Column
              body={mapperData => mapperRemoveButtonTemplate(mapperData)}
              style={{ width: "3%" }}
            />
          </DataTable>
        </div>
      )}
      <Dialog
        style={{ width: "min(100vw, 800px)", maxHeight: "400px" }}
        header={T("word.mappings")}
        visible={mapperMappingDialogVisibility}
        onHide={() => setMapperMappingDialogVisibility(false)}
        resizable
      >
        {selectedMapper && <MapperMappings selectedMapper={selectedMapper} />}
      </Dialog>
      {datasetMappersDialogVisibility && editorMode === MapperEditorMode.CREATE && (
        <CreateEditDatasetMapper
          mode={MapperEditorMode.CREATE}
          isVisible={datasetMappersDialogVisibility}
          setIsVisible={setDatasetMappersDialogVisibility}
          onSave={createDatasetMapper}
        />
      )}
      {selectedMapper && datasetMappersDialogVisibility && editorMode === MapperEditorMode.EDIT && (
        <CreateEditDatasetMapper
          mapperData={selectedMapper}
          mode={MapperEditorMode.EDIT}
          isVisible={datasetMappersDialogVisibility}
          setIsVisible={setDatasetMappersDialogVisibility}
          onSave={handleEditDialogSave}
        />
      )}
      <div className="w-12 flex justify-content-end mt-2">
        <Button
          className="component-editor-button icon-green"
          label={T("action.create-new")}
          icon="pi pi-plus"
          onClick={handleOpenCreateDialog}
        />
      </div>
    </div>
  );
};

export default ManageDatasetMappers;
